import { Button, ShowWhen } from "@/components/common";
import { useTheme } from "@/context/Theme.context";
import { ButtonColor, ButtonSize, Theme } from "@/enums";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import { HTMLProps } from "react";

type AdditionalProps = {
  title?: string;
  subtitle?: string;
  secondaryButtonText?: string;
  secondaryOnClick?: () => void;
  mainButton?: string;
  mainButtonOnClick?: () => void;
  mainBtnDisabled?: boolean;
  skipButtonOnClick?: () => void;
  stickyButtons?: boolean;
  noSubmit?: boolean;
};

type LayoutProps = HTMLProps<HTMLDivElement> & AdditionalProps;

const CommonFormLayout = ({
  title,
  subtitle,
  secondaryButtonText,
  mainButton,
  secondaryOnClick,
  mainButtonOnClick,
  mainBtnDisabled,
  skipButtonOnClick,
  stickyButtons,
  children,
  noSubmit,
}: LayoutProps) => {
  const t = useTranslations();
  const theme = useTheme();

  return (
    <div className="min-h-full w-fit">
      <div className="flex items-center justify-between">
        <ShowWhen condition={!!title}>
          <h1 className="mb-[18px] text-[40px] font-bold text-black">
            {title}
          </h1>
          <ShowWhen condition={!!skipButtonOnClick}>
            <Button
              color={ButtonColor.CUSTOM}
              className={classNames(
                "!w-fit bg-transparent text-lg font-bold uppercase",
                theme === Theme.UZ ? "uk-primary-color" : "text-[#0f6f7b]",
              )}
              buttonSize={ButtonSize.SM}
              onClick={skipButtonOnClick}
            >
              {t("skip")}
            </Button>
          </ShowWhen>
        </ShowWhen>
      </div>
      <ShowWhen condition={!!subtitle}>
        <h2 className="mb-[10px] max-w-[415px] text-lg font-normal leading-relaxed text-black opacity-50">
          {subtitle}
        </h2>
      </ShowWhen>
      <div className="flex w-fit flex-col">
        <div className="mb-[30px]">{children}</div>
        <div
          className={classNames("mt-auto", {
            "flex gap-[17px]": secondaryButtonText,
            "sticky bottom-0 border border-t-[1px] border-[#eeeeee] bg-white px-[3.125rem] pb-[1.25rem] pt-[1.25rem]":
              stickyButtons,
          })}
        >
          <ShowWhen condition={!!secondaryButtonText}>
            <Button
              color={
                secondaryButtonText === t("cancel")
                  ? ButtonColor.DANGER
                  : ButtonColor.SECONDARY
              }
              type="button"
              onClick={secondaryOnClick}
            >
              {secondaryButtonText}
            </Button>
          </ShowWhen>
          <ShowWhen condition={!!mainButton}>
            <Button
              type={noSubmit ? "button" : "submit"}
              disabled={mainBtnDisabled}
              onClick={mainButtonOnClick}
            >
              {mainButton}
            </Button>
          </ShowWhen>
        </div>
      </div>
    </div>
  );
};

export { CommonFormLayout };
